import { useTranslations } from 'next-intl';
import { CSSProperties } from 'react';
import { Link } from '@/shared/i18n/navigation';

type Props = {
    onReset: () => void;
    /** Override the default description (e.g. cart-specific copy). */
    description?: string;
    /** Show a "back to home" link. Defaults to true. */
    showHome?: boolean;
};

const wrapper: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    minHeight: '60vh',
    padding: '32px 24px',
    textAlign: 'center',
};

const title: CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    margin: 0,
};

const text: CSSProperties = {
    fontSize: '15px',
    lineHeight: 1.5,
    color: 'rgba(0, 0, 0, 0.6)',
    margin: 0,
    maxWidth: '420px',
};

const actions: CSSProperties = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '8px',
};

const button: CSSProperties = {
    appearance: 'none',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    background: '#111',
    color: '#fff',
};

const link: CSSProperties = {
    ...button,
    background: 'transparent',
    color: '#111',
    border: '1px solid rgba(0, 0, 0, 0.15)',
    textDecoration: 'none',
};

/**
 * Presentational fallback shared by the route-level `error.tsx` and component
 * ErrorBoundaries. Lives inside `NextIntlClientProvider`, so it can localize.
 */
export const ErrorFallback = ({ onReset, description, showHome = true }: Props) => {
    const t = useTranslations('ERROR_BOUNDARY');

    return (
        <div style={wrapper} role="alert">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="#111" strokeWidth="1.5" />
                <path d="M12 7v6" stroke="#111" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="12" cy="16.5" r="1" fill="#111" />
            </svg>
            <h1 style={title}>{t('TITLE')}</h1>
            <p style={text}>{description ?? t('DESCRIPTION')}</p>
            <div style={actions}>
                <button type="button" style={button} onClick={onReset}>
                    {t('RETRY')}
                </button>
                {showHome && (
                    <Link href="/" style={link}>
                        {t('HOME')}
                    </Link>
                )}
            </div>
        </div>
    );
};
